/*
 *  -----------------------------------------------------------------------
 *    <copyright file="manage-observation.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import cn from 'clsx';
import moment from 'moment';
import ActionDialog from '@components/core/action-dialog';
import TwoOptionsDialog from '../dialogs/two-options-dialog';
import { getDashboardPanels } from './manage-observation.helper';
import { ManageObservationProps } from './manage-observation.interface';
import { useBoolean, useDateTime, useTranslation } from 'hooks';
import {
    GET_CM_OBSERVATIONS_AND_ACTIONS,
    GET_DASHBOARDS,
    GET_PANELS_FOR_OBSERVATION,
    SAVE_CM_OBSERVATION,
} from 'queries/central-monitoring';
import { CentralMonitoringObservationArea } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import { validateDate, validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import DatePicker from 'views/components/core/date-picker';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';
import TextField from 'views/components/core/text-field';
import {
    getObservationTypeFromPanelId,
    getPanelId,
    makeObservationPanelsOptions,
    siteSummaryPanelId,
    studyOverviewPanelId,
} from '@utils/cm-observations.service';
import {
    getPanelsForObservation,
    getPanelsForObservationVariables,
    dashboards,
    dashboardsVariables,
} from '@queries/generated/central-monitoring.generated';
import { DataSetFields } from '@queries/generated/fragments.generated';
import { GET_SITES } from '@queries/sites';

const ManageObservation = ({
    study,
    open,
    onClose,
    currentObservation,
    panels,
    owners,
    sites,
    siteInfo,
    type,
    dataset,
    panelId,
    canEdit,
    datasetDates,
}: ManageObservationProps) => {
    const initialState = {
        id: null as any,
        description: '',
        panelId: '',
        type: null as any,
        potentialCause: '',
        confirmedCause: '',
        ownerId: null as any,
        closedDate: null as any,
        observationDate: null as any,
        comments: '',
        siteId: Constants.ObjectId.Empty,
    };

    const { t } = useTranslation();
    const date = useDateTime();

    const [saveObservationMutation] = useMutation(SAVE_CM_OBSERVATION);
    const [loadPanels, { data: panelsData }] = useLazyQuery<getPanelsForObservation, getPanelsForObservationVariables>(
        GET_PANELS_FOR_OBSERVATION,
    );
    const [loadDashboards, { data: dashboardsData }] = useLazyQuery<dashboards, dashboardsVariables>(GET_DASHBOARDS);

    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const isSiteRequired = useBoolean(false);
    const isSiteDisabled = useBoolean(false);
    const showWarningDialog = useBoolean(false);

    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({} as any);
    const [ownerOptions, setOwnerOptions] = useState([] as SelectWithSearchDataArrayInterface[]);
    const [panelsOptions, setPanelsOptions] = useState<SelectWithSearchDataArrayInterface[]>([]);
    const [updatedItemsMessage, setUpdatedItemsMessage] = useState('');

    const datasetDate =
        (currentObservation && date.displayDate(currentObservation._datasetDate)) || dataset?._dataCutDate;

    const [loadSitesData, { loading: loadingSiteSnapshotData, data: siteSnapshotData }] = useLazyQuery(GET_SITES);

    useEffect(() => {
        if (owners) {
            const array = [...owners];
            array.unshift({
                id: Constants.ObjectId.Empty,
                text: t('selectOwner'),
                default: true,
            });
            setOwnerOptions(array);
        }
    }, [owners]);

    useEffect(() => {
        if (currentObservation && open) {
            const processPanelsDate = datasetDates?.find(
                (set: DataSetFields) => set.id === currentObservation.dataSetId,
            )?.processPanelsDate;

            loadPanels({
                variables: {
                    organisationId: study?.organisation?.id,
                    studyId: study?.id,
                    timestamp: processPanelsDate,
                },
            });

            loadDashboards({
                variables: {
                    studyId: study?.id,
                    timestamp: processPanelsDate,
                },
            });
        } else setPanelsOptions(makeObservationPanelsOptions(panels));
    }, [currentObservation, datasetDates, open]);

    useEffect(() => {
        if (panelsData && dashboardsData && currentObservation) {
            const panelsForObservation = getDashboardPanels(dashboardsData.dashboards, panelsData.panels);
            setPanelsOptions(makeObservationPanelsOptions(panelsForObservation));
        }
    }, [panelsData, dashboardsData, currentObservation]);

    useEffect(() => {
        if (open && currentObservation) {
            const dataSetTimeStamp = datasetDates?.find(
                (set: DataSetFields) => set.id === currentObservation.dataSetId,
            )?.created;
            loadSitesData({ variables: { studyId: study.id, timestamp: dataSetTimeStamp } });
        }
    }, [currentObservation, open]);

    useEffect(() => {
        isSiteDisabled.setValue(type === CentralMonitoringObservationArea.STUDY_OVERVIEW);
    }, [type]);

    useEffect(() => {
        if (open) {
            const updatedPanelId =
                panelId && panelId !== Constants.ObjectId.Empty ? panelId : getPanelId(currentObservation?.panel, type);

            setState({
                id: currentObservation?.id || null,
                panelId: updatedPanelId,
                type,
                siteId: currentObservation?.siteId || siteInfo?.id || state.siteId,
                description: currentObservation?.description || state.description,
                ownerId: currentObservation?.ownerId || Constants.ObjectId.Empty,
                comments: currentObservation?.comments || state.comments,
                potentialCause: currentObservation?.potentialCause || state.potentialCause,
                confirmedCause: currentObservation?.confirmedCause || state.confirmedCause,
                observationDate:
                    formatDateUTC_DEPRECATED(currentObservation?.observationDate, true) || state.observationDate,
                closedDate: formatDateUTC_DEPRECATED(currentObservation?.closedDate, true) || state.closedDate,
            });

            isSiteRequired.setValue(updatedPanelId === siteSummaryPanelId);
        }
    }, [currentObservation, open]);

    const sitesData = () => {
        let sitesToMap = [];
        if (
            currentObservation &&
            (!dataset || currentObservation?.dataSet.id !== dataset?.id) &&
            !loadingSiteSnapshotData &&
            siteSnapshotData?.sites
        ) {
            sitesToMap = siteSnapshotData?.sites;
        } else {
            sitesToMap = sites;
        }
        const mappedSites =
            sitesToMap
                ?.filter((item) => study?.sitesStatusesToExclude?.indexOf(item.status) < 0)
                .map((item) => ({
                    id: item.id,
                    text: item.name,
                })) ?? [];
        mappedSites.unshift({
            id: Constants.ObjectId.Empty,
            text: t('ra:selectValue'),
        });
        return mappedSites;
    };

    const isSiteIdInvalid = () =>
        isSiteRequired.value && (state.siteId === null || state.siteId === Constants.ObjectId.Empty);

    useEffect(() => {
        setErrors({
            ...errors,
            siteId: isSiteIdInvalid() ? t('ra:requiredFieldError') : null,
        });
    }, [isSiteRequired, state.siteId]);

    const handleClose = (action?: string) => {
        if (action !== 'error') {
            setState(initialState);
            setErrors({});
            disabledSave.setTrue();
            isSiteRequired.setFalse();
        }

        showWarningDialog.setFalse();
        onClose(action);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string, required?: boolean) => (event: any) => {
        disabledSave.setFalse();
        const length = 1200;

        setState({
            ...state,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, required);
    };

    const onChangePanelId = (name: string) => (event: any) => {
        disabledSave.setFalse();

        const { value } = event.target;
        const updatedType = getObservationTypeFromPanelId(value);

        setState({
            ...state,
            panelId: value,
            type: updatedType,
        });
        validate(value, name, undefined, true);

        isSiteRequired.setValue(value === siteSummaryPanelId);
        isSiteDisabled.setValue(value === studyOverviewPanelId);
    };

    const onChangeDate = (name: string) => (event: any) => {
        disabledSave.setFalse();
        if (name === 'observationDate')
            setErrors({
                ...errors,
                observationDate: null,
            });
        setState({
            ...state,
            [name]: event?.value ? moment(event?.value) : null,
        });
    };

    const saveObservation = () => {
        saving.setTrue();
        const data: any = {
            ...state,
            type: getObservationTypeFromPanelId(state.panelId),
            // remove helper text for id
            panelId: state.panelId.startsWith(Constants.ObjectId.Empty) ? Constants.ObjectId.Empty : state.panelId,
        };

        const refetchQueries: any[] = [];
        if (siteInfo || dataset)
            refetchQueries.push({
                query: GET_CM_OBSERVATIONS_AND_ACTIONS,
                variables: {
                    studyId: study.id,
                    siteId: siteInfo?.id,
                },
            });
        else
            refetchQueries.push({
                query: GET_CM_OBSERVATIONS_AND_ACTIONS,
                variables: {
                    studyId: study.id,
                },
            });
        saveObservationMutation({
            variables: {
                observation: {
                    ...data,
                    siteId: state.siteId ?? siteInfo?.id,
                    dataSetId: dataset?.id ?? currentObservation?.dataSetId,
                    observationDate: formatDateUTC_DEPRECATED(state?.observationDate),
                    closedDate: formatDateUTC_DEPRECATED(state?.closedDate),
                    studyId: study.id,
                },
            },
            refetchQueries,
        })
            .then(() => {
                saving.setFalse();
                handleClose(currentObservation ? 'edit' : 'add');
            })
            .catch(() => {
                saving.setFalse();
                handleClose('error');
            });
    };

    const validateDates = () => validateDate(state.closedDate) && validateDate(state.observationDate);

    const checkFieldValid = (value: boolean, field: string) => {
        if (!value) {
            setErrors({
                ...errors,
                [field]: t('valueInvalid'),
            });
        }
    };

    const hasPanelNameChanged = () => {
        if (currentObservation.panelId !== Constants.ObjectId.Empty)
            return currentObservation.panelId !== state.panelId;

        if (!state.panelId.startsWith(Constants.ObjectId.Empty)) return true;

        return type !== state.type;
    };

    const shouldShowWarningDialog = () => {
        const panelNameChanged = hasPanelNameChanged();
        const siteChanged = currentObservation.siteId !== state.siteId;

        if (!panelNameChanged && !siteChanged) return false;

        if (panelNameChanged && siteChanged) setUpdatedItemsMessage(t('cm:panelAndSiteUpdate'));
        else if (panelNameChanged) setUpdatedItemsMessage(t('cm:panelUpdate'));
        else setUpdatedItemsMessage(t('cm:siteUpdate'));

        return true;
    };

    const onSave = () => {
        const descriptionError = validateFields(state.description, 'description', 1200, true);
        const observationDateError = validateFields(state.observationDate, 'observationDate', undefined, true);

        const siteIdInvalid = isSiteIdInvalid();

        setErrors({
            ...errors,
            ...descriptionError,
            ...observationDateError,
            siteId: siteIdInvalid ? t('ra:requiredFieldError') : null,
        });

        if (descriptionError?.description || observationDateError?.observationDate || !validateDates() || siteIdInvalid)
            return;

        if (currentObservation && shouldShowWarningDialog()) {
            showWarningDialog.setTrue();
        } else saveObservation();
    };

    const getDialogTitle = () => {
        const idText = currentObservation?.recordId ? `: ${currentObservation.recordId}` : '';

        return idText ? `${t('ra:editObservation')}${idText}` : t('ra:addObservation');
    };

    const showPanelNotFoundError =
        open &&
        type === CentralMonitoringObservationArea.PANEL &&
        !currentObservation &&
        !panels.some((panel) => panel.id === panelId);

    return showPanelNotFoundError ? (
        <ActionDialog title="Panel not found" onClose={handleClose} asNotification className="action-dialog-small">
            <p>Panel could not be found. Please refresh and try again.</p>
        </ActionDialog>
    ) : (
        <div>
            {showWarningDialog.value && (
                <TwoOptionsDialog
                    title={getDialogTitle()}
                    handleClose={() => showWarningDialog.setFalse()}
                    submitText={t('continue')}
                    handleSubmit={saveObservation}
                    className="action-dialog-small"
                >
                    <>
                        <p>{t('cm:editObservationWarning', { updatedItems: updatedItemsMessage })}</p>
                        <p>{t('wishToContinue')}</p>
                    </>
                </TwoOptionsDialog>
            )}
            <CustomDialog
                onClose={handleClose}
                aria-labelledby="add-action"
                fullWidth
                maxWidth="md"
                scroll="paper"
                open={open}
            >
                <DialogTitle id="add-action-title">
                    {getDialogTitle()}
                    <button type="button" className="modal-close-btn" onClick={() => handleClose()}>
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            style={{ fontSize: '24px' }}
                            data-testid="close-dialog"
                        />
                    </button>
                </DialogTitle>
                <DialogContent style={{ padding: 15, paddingTop: 20 }}>
                    <div className="checkbox-info-wrap">
                        <div>
                            <div>
                                <span className="title">{t('cm:datasetDate')}:</span>
                                {datasetDate}
                            </div>
                        </div>
                        <span>
                            <p className="actions-text">
                                {currentObservation && currentObservation._openAction > 0
                                    ? `${t('ra:openActions')}: ${currentObservation._openAction} / ${
                                          currentObservation._numberOfActions
                                      } | `
                                    : ''}
                                {currentObservation && currentObservation._recentActionDate
                                    ? `${t('ra:recentAction')}: ${moment(currentObservation._recentActionDate).format(
                                          'DD-MMM-YYYY',
                                      )}`
                                    : ''}
                            </p>
                        </span>
                    </div>
                    <div className="selects-wrapper selects-wrapper--solo">
                        <SelectWithCategories
                            label={`${t('cm:panelName')}*`}
                            dataArray={panelsOptions}
                            onChange={onChangePanelId}
                            fieldSelect="panelId"
                            currentState={state.panelId}
                            error={errors.panelId}
                            searchable
                            filterable={false}
                            showWarning={false}
                            disabled={!currentObservation?.recordId}
                            customClassName="form-action-type"
                            appendPopupToSelect
                        />
                    </div>
                    <div className="selects-wrapper">
                        <SelectWithCategories
                            label={`${t('site')}${isSiteRequired.value ? '*' : ''}`}
                            dataArray={sitesData()}
                            onChange={onChange}
                            fieldSelect="siteId"
                            currentState={state.siteId}
                            error={errors.siteId}
                            searchable
                            filterable={false}
                            labelField="site"
                            showWarning={false}
                            customClassName="form-action-type mr"
                            disabled={isSiteDisabled.value}
                        />
                        <FormControl variant="outlined" className={cn('form-action-type')}>
                            <DatePicker
                                label={`${t('ra:observationDate')}*`}
                                error={errors.observationDate}
                                customId="edit-observation-observation-date"
                                labelWidth={145}
                                selectedDate={state.observationDate}
                                handleDateChange={onChangeDate('observationDate')}
                                setValid={(value: boolean) => checkFieldValid(value, 'observationDate')}
                            />
                            {errors.observationDate && <p className="request-date-error">{errors.observationDate}</p>}
                        </FormControl>
                    </div>
                    <TextField
                        label={`${t('ra:observation')}*`}
                        id="edit-observation-description"
                        multiline
                        rows={5}
                        fullWidth
                        value={state.description}
                        onChange={onChange('description', true)}
                        margin="normal"
                        placeholder={t('ra:enterObservation')}
                        helperText={
                            <CharactersRemaining
                                id="edit-observation-description"
                                length={state.description.length}
                                max={1200}
                                error={errors.description}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        className="mb"
                        error={!!errors.description}
                    />
                    <TextField
                        label={t('ra:potentialCause')}
                        id="edit-observation-potential-cause"
                        multiline
                        rows={5}
                        fullWidth
                        value={state.potentialCause}
                        onChange={onChange('potentialCause')}
                        size="small"
                        placeholder={t('ra:enterPotentialCause')}
                        helperText={
                            <CharactersRemaining
                                id="edit-observation-potentialCause"
                                length={state.potentialCause.length}
                                max={1200}
                                error={errors.potentialCause}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        className="mb"
                        error={!!errors.potentialCause}
                    />
                    <TextField
                        label={t('ra:confirmedCause')}
                        id="edit-observation-confirmed-cause"
                        multiline
                        rows={5}
                        fullWidth
                        value={state.confirmedCause}
                        onChange={onChange('confirmedCause')}
                        size="small"
                        placeholder={t('ra:enterConfirmedCause')}
                        helperText={
                            <CharactersRemaining
                                id="edit-observation-confirmedCause"
                                length={state.confirmedCause.length}
                                max={1200}
                                error={errors.confirmedCause}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        className="mb"
                        error={!!errors.confirmedCause}
                    />
                    <div className="selects-wrapper flex-end">
                        <FormControl variant="outlined" className="form-action-type mr">
                            <DatePicker
                                label={t('ra:closedDate')}
                                customId="edit-observation-closed-date"
                                labelWidth={105}
                                error={errors.closedDate}
                                disabled={Boolean(currentObservation?._isClosedDateReadOnly)}
                                selectedDate={state.closedDate}
                                handleDateChange={onChangeDate('closedDate')}
                                setValid={(value: boolean) => checkFieldValid(value, 'closedDate')}
                            />
                        </FormControl>
                        <SelectWithCategories
                            label={t('owner')}
                            dataArray={ownerOptions}
                            sortDescriptors={[{ field: 'text', dir: 'asc' }]}
                            onChange={onChange}
                            fieldSelect="ownerId"
                            currentState={state.ownerId}
                            searchable
                            labelField="owner"
                            showWarning={
                                currentObservation &&
                                currentObservation._owner &&
                                currentObservation._owner.id === state.ownerId &&
                                currentObservation._owner.active === false
                            }
                            customClassName={cn('form-action-type', {
                                inactive:
                                    currentObservation &&
                                    currentObservation._owner &&
                                    currentObservation._owner.id === state.ownerId &&
                                    currentObservation._owner.active === false,
                            })}
                        />
                    </div>
                    <TextField
                        label={t('ra:comments')}
                        id="edit-observation-comments"
                        multiline
                        rows={5}
                        fullWidth
                        value={state.comments}
                        onChange={onChange('comments')}
                        size="small"
                        placeholder={t('ra:enterComments')}
                        helperText={
                            <CharactersRemaining
                                id="edit-observation-comments"
                                length={state.comments.length}
                                max={1200}
                                error={errors.comments}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        className="mb"
                        error={!!errors.comments}
                    />
                    <DialogActions
                        className={cn('actions-info-wrapper-edit-cv', { 'flex-end': !currentObservation?.recordId })}
                    >
                        <ModalUpdatedInfo item={currentObservation} />
                        <SaveButton
                            className="edit-cv-update-btn"
                            id="edit-observation-save"
                            text={currentObservation ? 'common:update' : 'create'}
                            disabled={
                                Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                                disabledSave.value ||
                                saving.value ||
                                !canEdit
                            }
                            onClick={onSave}
                        />
                    </DialogActions>
                </DialogContent>
            </CustomDialog>
        </div>
    );
};

export default ManageObservation;
